const MAX_ERROR_LENGTH = 500;

export const API_ERROR_MESSAGES = {
  requestFailed: "We couldn't process your request. Please try again.",
  server: "Oops! Something went wrong. Please try again later.",
};

export const BRAND_GUIDELINE_HINT =
  "This prompt does not match Kalahari Resorts brand guidelines. Try prompts about real guest experiences: waterpark fun, family activities, spa, dining, or conventions.";

const GEMINI_REFUSAL_START =
  /I (?:cannot fulfill this request|am unable to (?:create|generate)|'m unable to (?:create|generate)|can't (?:create|generate)|cannot (?:create|generate))/i;

const GEMINI_TECHNICAL_STOP =
  /\b(finish_reason|FinishReason\.|model_version=|candidates_token_count|prompt_token_count|sdk_http_response|usage_metadata)/i;

const unescapeJsonString = (value) =>
  String(value ?? "")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\n/g, "\n")
    .replace(/\\\\/g, "\\");

const truncate = (text, max = MAX_ERROR_LENGTH) => {
  const value = String(text ?? "").trim();
  if (!value) return "";
  if (value.length <= max) return value;
  return `${value.slice(0, max - 3)}...`;
};

const isObject = (value) => value !== null && typeof value === "object";

const isAbortLikeError = (error) =>
  error?.name === "AbortError" ||
  error?.name === "CanceledError" ||
  error?.code === "ERR_CANCELED" ||
  /aborted|canceled|cancelled/i.test(String(error?.message ?? ""));

const getStatusFromError = (error) =>
  error?.status ??
  error?.response?.status ??
  error?.response?.data?.status ??
  error?.body?.status ??
  null;

const getResponseBodyFromError = (error) =>
  error?.response?.data ?? error?.body ?? error?.data ?? null;

const hasTechnicalMessage = (message) =>
  /stack trace|exception|sql|java\.|org\.|traceback|undefined|null pointer|ECONN|ENOTFOUND|ERR_|internal_server_error/i.test(
    String(message ?? ""),
  );

export const normalizeApiError = (
  error,
  fallback = API_ERROR_MESSAGES.requestFailed,
) => {
  if (error?.normalizedError) {
    return error.normalizedError;
  }

  const status = getStatusFromError(error);
  let category = "unknown";
  let message = fallback;

  if (isAbortLikeError(error)) {
    category = "canceled";
    message = "";
  } else if (Number(status) >= 500) {
    category = "server";
    message = API_ERROR_MESSAGES.server;
  } else if (status) {
    category = "api";
    const extracted = extractApiErrorMessage(
      getResponseBodyFromError(error) ?? { message: error?.message },
      fallback,
    );
    message = hasTechnicalMessage(extracted) ? fallback : extracted;
  } else {
    const extracted = extractApiErrorMessage(
      getResponseBodyFromError(error) ?? { message: error?.message },
      fallback,
    );
    message = hasTechnicalMessage(extracted) ? fallback : extracted;
  }

  if (!message && category !== "canceled") {
    message = fallback;
  }

  return {
    category,
    status,
    message,
    userMessage: message,
    isCanceled: category === "canceled",
  };
};

export const decorateApiError = (
  error,
  fallback = API_ERROR_MESSAGES.requestFailed,
) => {
  if (!isObject(error)) {
    const wrapped = new Error(String(error || fallback));
    wrapped.normalizedError = normalizeApiError(wrapped, fallback);
    wrapped.userMessage = wrapped.normalizedError.userMessage;
    wrapped.status = wrapped.normalizedError.status;
    return wrapped;
  }

  const normalizedError = normalizeApiError(error, fallback);
  error.normalizedError = normalizedError;
  error.userMessage = normalizedError.userMessage;
  if (
    (normalizedError.category === "server" ||
      normalizedError.category === "api") &&
    normalizedError.userMessage
  ) {
    error.message = normalizedError.userMessage;
  }
  error.status = error.status ?? normalizedError.status;
  error.category = normalizedError.category;
  error.isCanceled = normalizedError.isCanceled;
  return error;
};

export const getUserErrorMessage = (
  error,
  fallback = API_ERROR_MESSAGES.requestFailed,
) => normalizeApiError(error, fallback).userMessage || fallback;

export const logApiError = (context, error) => {
  if (typeof console === "undefined") return;
  const label = context ? `[${context}]` : "[API Error]";
  console.error(label, error);
};

const normalizeRefusalText = (text) => {
  let refusal = String(text ?? "")
    .replace(/\s+/g, " ")
    .trim();

  if (!refusal) return "";

  if (!/[.!?]$/.test(refusal)) {
    refusal = `${refusal}.`;
  }

  const sentences = refusal.match(/[^.!?]+[.!?]+/g);
  if (sentences?.length) {
    return truncate(sentences.slice(0, 2).join(" "));
  }

  return truncate(refusal);
};

/** Extract model refusal from Gemini SDK-style payloads (Part(text='...')). */
const extractPartTextFromPayload = (text) => {
  const source = String(text ?? "");
  const matches = [...source.matchAll(/text='((?:[^'\\]|\\.)*)'/g)];

  for (const match of matches) {
    const partText = unescapeJsonString(match[1]).trim();
    if (GEMINI_REFUSAL_START.test(partText) || /brand guidelines/i.test(partText)) {
      return partText;
    }
  }

  return matches[0] ? unescapeJsonString(matches[0][1]).trim() : null;
};

const extractGeminiRefusal = (text) => {
  const source = String(text ?? "");

  const partText = extractPartTextFromPayload(source);
  if (partText && GEMINI_REFUSAL_START.test(partText)) {
    return normalizeRefusalText(partText);
  }

  const refusalIdx = source.search(GEMINI_REFUSAL_START);
  if (refusalIdx < 0) return null;

  let refusal = source.slice(refusalIdx);
  const endIdx = refusal.search(GEMINI_TECHNICAL_STOP);
  if (endIdx > 40) {
    refusal = refusal.slice(0, endIdx).trim();
  }

  return normalizeRefusalText(refusal);
};

export const isBrandGuidelineError = (text) =>
  /Kalahari Resorts|does not align with the approved|brand guidelines|guest experiences at Kalahari|operational realities|authentic guest experiences/i.test(
    String(text ?? ""),
  );

/** True when a generate response body is an error payload, not media/content. */
export const isGenerateErrorPayload = (text) => {
  const source = String(text ?? "");
  if (!source.trim()) return false;

  return (
    /Gemini image parsing failed/i.test(source) ||
    /Python API Failed/i.test(source) ||
    /INTERNAL_SERVER_ERROR/i.test(source) ||
    GEMINI_REFUSAL_START.test(source) ||
    (isBrandGuidelineError(source) && /Part\(|candidates=\[/i.test(source))
  );
};

const formatRefusalMessage = (refusal, fullText) => {
  if (!refusal) return null;
  return isBrandGuidelineError(fullText)
    ? `${refusal}\n\n${BRAND_GUIDELINE_HINT}`
    : refusal;
};

const collectCandidateText = (body) => {
  if (body == null) return "";

  if (typeof body === "string") {
    return body;
  }

  return [body.message, body.detail, body.error, body.title]
    .filter(Boolean)
    .map((value) => String(value))
    .join("\n");
};

/**
 * Pull a user-facing message from API error JSON (including nested Python/Gemini payloads).
 */
export const extractApiErrorMessage = (body, fallback = "Request failed.") => {
  if (body == null) return fallback;

  if (typeof body === "string") {
    return extractApiErrorMessage({ message: body }, fallback);
  }

  const combined = collectCandidateText(body);
  if (!combined.trim()) return fallback;

  const refusal = extractGeminiRefusal(combined);
  const refusalMessage = formatRefusalMessage(refusal, combined);
  if (refusalMessage) return refusalMessage;

  if (isBrandGuidelineError(combined)) {
    return BRAND_GUIDELINE_HINT;
  }

  let text = String(body.message ?? body.detail ?? body.error ?? body.title ?? "")
    .trim();
  if (!text) return fallback;

  text = text.replace(/^Python API Failed:\s*/i, "").trim();

  if (text.startsWith("{")) {
    try {
      const nested = JSON.parse(text);
      return extractApiErrorMessage(nested, fallback);
    } catch {
      /* keep text */
    }
  }

  const detailMatch = text.match(/"detail"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (detailMatch) {
    const detailText = unescapeJsonString(detailMatch[1]);
    const nestedRefusal = extractGeminiRefusal(detailText);
    const nestedMessage = formatRefusalMessage(nestedRefusal, detailText);
    if (nestedMessage) return nestedMessage;
    return extractApiErrorMessage({ message: detailText }, fallback);
  }

  const singleQuoteDetail = text.match(/'detail'\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (singleQuoteDetail) {
    const detailText = unescapeJsonString(singleQuoteDetail[1]);
    const nestedRefusal = extractGeminiRefusal(detailText);
    const nestedMessage = formatRefusalMessage(nestedRefusal, detailText);
    if (nestedMessage) return nestedMessage;
    return extractApiErrorMessage({ message: detailText }, fallback);
  }

  text = text
    .replace(/Gemini image parsing failed[^.]*\.?\s*/gi, "")
    .replace(/Response was stopped with FinishReason\.[A-Z_]+\.?\s*/gi, "")
    .replace(/Finish message:\s*/gi, "")
    .replace(/Content\(.*?\)\s*was stopped\.?\s*/gi, "")
    .replace(/sdk_http_response=[^)]*\)?\s*/gi, "")
    .replace(/candidates=\[[^\]]*\]\s*/gi, "")
    .trim();

  if (!text) return fallback;

  if (
    /INTERNAL_SERVER_ERROR/i.test(combined) &&
    isBrandGuidelineError(combined)
  ) {
    const lateRefusal = extractGeminiRefusal(combined);
    return formatRefusalMessage(lateRefusal, combined) ?? BRAND_GUIDELINE_HINT;
  }

  return truncate(text) || fallback;
};

export const readFetchError = async (response, fallback) => {
  const status = response?.status;
  const defaultFallback =
    fallback ?? (status ? `Request failed (${status}).` : "Request failed.");

  try {
    const raw = await response.text();
    if (!raw?.trim()) {
      const error = new Error(defaultFallback);
      error.status = status;
      return decorateApiError(error, defaultFallback);
    }

    try {
      const json = JSON.parse(raw);
      const message = extractApiErrorMessage(json, defaultFallback);
      const error = new Error(message);
      error.status = status;
      error.body = json;
      error.isPolicyRejection =
        isBrandGuidelineError(collectCandidateText(json)) ||
        isGenerateErrorPayload(collectCandidateText(json));
      return decorateApiError(error, defaultFallback);
    } catch {
      const message = extractApiErrorMessage(raw, defaultFallback);
      const error = new Error(message);
      error.status = status;
      error.isPolicyRejection =
        isBrandGuidelineError(raw) || isGenerateErrorPayload(raw);
      return decorateApiError(error, defaultFallback);
    }
  } catch {
    return decorateApiError(new Error(defaultFallback), defaultFallback);
  }
};
